/**
 * Order-entry quantity: bounds, preset chips, and the user's default.
 *
 * This is the ONLY place the preset list and the quantity guards are allowed to
 * live. The chips used to be hardcoded in three separate spots (TradingPanel's
 * desktop chips, TradingPanel's mobile settings panel, and useTradingLogic's
 * memo), which is exactly how the desktop and mobile order entry drift apart.
 *
 * The guards matter more than they look: a 0-qty order is a silent no-op that
 * looks like the click didn't register, and a fat-fingered 1000 is real money.
 * Garbage is REJECTED (the caller keeps its previous value) rather than coerced,
 * so nothing ever persists NaN into the preferences blob.
 */

/** Smallest order the UI will ever hand to a broker. */
export const MIN_ORDER_QUANTITY = 1;

/** Sanity ceiling for the default. Not an exchange limit — a fat-finger guard. */
export const MAX_ORDER_QUANTITY = 100;

/** Used when nothing valid is stored. Matches the pre-existing hardcoded `1`. */
export const FALLBACK_ORDER_QUANTITY = 1;

/** The preset chips shown in every quantity control. */
export const QUANTITY_PRESETS: readonly number[] = [1, 2, 3, 5, 10];

/**
 * Coerce to a number without JS's helpful-but-wrong conversions:
 * `Number('')`, `Number(null)` and `Number([])` are all 0, and 0 is a quantity
 * we must reject rather than silently accept.
 */
function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return NaN;
}

/**
 * True when `value` is a usable order quantity: a whole number inside the
 * bounds. Rejects NaN, Infinity, 0, negatives, fractions and absurd sizes.
 */
export function isValidOrderQuantity(value: unknown): boolean {
  const n = toNumber(value);
  return Number.isInteger(n) && n >= MIN_ORDER_QUANTITY && n <= MAX_ORDER_QUANTITY;
}

/**
 * Return `value` if it is a valid quantity, else `fallback` — and if the
 * fallback is itself junk, the module-level constant. A bad stored preference
 * can therefore never leak into an order ticket.
 */
export function sanitizeOrderQuantity(
  value: unknown,
  fallback: unknown = FALLBACK_ORDER_QUANTITY,
): number {
  if (isValidOrderQuantity(value)) return toNumber(value);
  if (isValidOrderQuantity(fallback)) return toNumber(fallback);
  return FALLBACK_ORDER_QUANTITY;
}

/**
 * Clamp for the +/- steppers, where the user is nudging an existing value and
 * running off the end should stop at the edge rather than snap back to 1.
 */
export function clampOrderQuantity(value: unknown, fallback: unknown = FALLBACK_ORDER_QUANTITY): number {
  const n = toNumber(value);
  if (!Number.isFinite(n)) return sanitizeOrderQuantity(fallback);
  const whole = Math.round(n);
  return Math.min(MAX_ORDER_QUANTITY, Math.max(MIN_ORDER_QUANTITY, whole));
}

/**
 * The preset chips, with the user's default folded in so a default that isn't
 * one of the stock presets (say 4) still gets a chip and can still light up as
 * "active". Sorted ascending, no duplicates.
 */
export function quantityPresetsWith(defaultQuantity: unknown): number[] {
  const resolved = sanitizeOrderQuantity(defaultQuantity);
  const merged = new Set<number>([...QUANTITY_PRESETS, resolved]);
  return [...merged].sort((a, b) => a - b);
}
